import React from "react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";

/**
 * Privacy Policy — Square & Compass Lodge Management
 *
 * Public, unauthenticated route. Linked from:
 *  - mobile app onboarding & sign-up
 *  - App Store / Google Play store metadata
 *  - footer of the web admin
 *
 * Kept intentionally on a static React route (rather than a CMS) so
 * compliance reviewers (Apple, Google) always find it at a stable URL.
 */
export default function PrivacyPolicyPage() {
  const { t, i18n } = useTranslation();
  const en = i18n.language?.startsWith("en");

  const updatedDate = t("privacyPolicy.may_13_2026");

  // ---------------- TEXTS ----------------
  const T = en
    ? {
        title: "Privacy Policy",
        intro:
          "Square & Compass Lodge Management (\"the Service\") is a multi-tenant administration platform for Masonic lodges. This Privacy Policy explains what personal data we collect, why we collect it, how we protect it, and what rights you have under the EU General Data Protection Regulation (GDPR) and equivalent laws.",
        sections: [
          {
            h: "1. Controller",
            p: "The Service is operated by Square & Compass (\"we\", \"us\"). For matters concerning the processing of your personal data, contact us at contact@squareandcompass.store.",
          },
          {
            h: "2. Data we collect",
            p: "When you create or are added to a lodge account, we process the following categories of personal data: full name, email address, phone number, date of birth, date of initiation, masonic degree, lodge affiliation, masonic passport number, profile photograph, NFC token, scanned documents (passport, diplomas, transfer letters), and chat messages exchanged within the lodge. We also collect technical data automatically: IP address, device type and OS, timestamps of access, app version. We do NOT collect biometric data (Face ID / Touch ID is verified locally on your device and never transmitted to our servers).",
          },
          {
            h: "3. Why we process it (lawful basis)",
            p: "We process personal data on the following lawful bases: (a) contract — to provide the lodge management features you request, including dues, attendance, transfers, and online meetings; (b) legal obligation — to comply with applicable accounting and audit laws; (c) legitimate interests — to protect our service against fraud and unauthorised access; (d) consent — where you explicitly opt in to features such as push notifications or voice journal transcription.",
          },
          {
            h: "4. Encryption & security",
            p: "Personally Identifiable Information (PII) — including name, email, phone number, and contact data — is encrypted at the database field level using AES-256 with a per-tenant key. Passwords are hashed with bcrypt (cost factor 12). Multi-Factor Authentication (TOTP) is mandatory for administrative roles. All network traffic between you and our servers is encrypted with TLS 1.3 (HTTPS), and our domain is enrolled in the HSTS preload list. Every document generated by the Service (diplomas, certificates, transfers) is signed with a SHA-256 hash that includes the issuer's IP and timestamp, creating an immutable audit trail.",
          },
          {
            h: "5. Who has access",
            p: "Your data is visible only to: (i) you; (ii) the officers of the lodge you belong to (in roles strictly necessary, such as the Secretary or Treasurer); (iii) the SuperAdministrator of your Grand Lodge, if any; (iv) our infrastructure providers (MongoDB Atlas, hosting provider Emergent) acting strictly as data processors under signed DPAs. We never sell or rent personal data, and we do not share it with advertisers.",
          },
          {
            h: "6. International transfers",
            p: "Data is stored in the European Economic Area. If we ever transfer data outside the EEA, we will rely on European Commission adequacy decisions or Standard Contractual Clauses and notify you in advance.",
          },
          {
            h: "7. Retention",
            p: "We retain your data for as long as your lodge account is active. When your membership ends or you request deletion, we delete or fully anonymise your data within 30 days, except where retention is required by accounting or audit law (typically 5–10 years for financial records), in which case the data is moved to a restricted archive accessible only for legal compliance.",
          },
          {
            h: "8. Your rights",
            p: "Under GDPR you have the right to: (a) access your personal data; (b) request rectification of inaccurate data; (c) request erasure (\"right to be forgotten\"); (d) request restriction of processing; (e) data portability — receive a copy of your data in a machine-readable format; (f) object to processing; (g) withdraw consent at any time; (h) lodge a complaint with a supervisory authority (in Romania: ANSPDCP). To exercise any of these rights, write to contact@squareandcompass.store. We respond within 30 days.",
          },
          {
            h: "9. Push notifications & analytics",
            p: "Push notifications are optional and require your explicit consent at first launch. You can revoke this consent at any time in your phone settings. The Service uses minimal first-party analytics (counts of feature use, error logs) — we do NOT use Google Analytics, Facebook Pixel, or any third-party advertising tracker.",
          },
          {
            h: "10. Children",
            p: "The Service is not intended for users under 18 years of age. Masonic lodges are by their nature adult organisations. We do not knowingly collect data from minors.",
          },
          {
            h: "11. Changes to this Policy",
            p: "We will notify you of material changes to this Policy through an in-app notice or email at least 14 days before they take effect. The date of the latest revision is shown at the top of this page.",
          },
          {
            h: "12. Contact",
            p: "Square & Compass Lodge Management · contact@squareandcompass.store · https://www.squareandcompass.store",
          },
        ],
      }
    : {
        title: "Politica de Confidențialitate",
        intro:
          "Square & Compass Lodge Management („Serviciul”) este o platformă multi-tenant de administrare pentru loji masonice. Această Politică de Confidențialitate explică ce date personale colectăm, de ce le colectăm, cum le protejăm și ce drepturi aveți conform Regulamentului General privind Protecția Datelor (GDPR) și legilor echivalente.",
        sections: [
          {
            h: "1. Operatorul de date",
            p: "Serviciul este operat de Square & Compass („noi”). Pentru întrebări privind prelucrarea datelor dvs. personale, ne puteți contacta la contact@squareandcompass.store.",
          },
          {
            h: "2. Datele pe care le colectăm",
            p: "Când vă creați un cont sau sunteți adăugat într-o lojă, prelucrăm următoarele categorii de date personale: nume complet, adresă de email, număr de telefon, data nașterii, data inițierii, grad masonic, afilierea la lojă, numărul pașaportului masonic, fotografia de profil, token-ul NFC, documente scanate (pașaport, diplome, scrisori de transfer) și mesaje de chat schimbate în cadrul lojei. Colectăm și date tehnice automat: adresa IP, tipul de dispozitiv și OS, marca temporală a accesării, versiunea aplicației. NU colectăm date biometrice (Face ID / Touch ID este verificat local pe dispozitivul dvs. și nu este transmis niciodată către serverele noastre).",
          },
          {
            h: "3. De ce prelucrăm datele (baza legală)",
            p: "Prelucrăm date personale pe următoarele baze legale: (a) contract — pentru a furniza funcțiile de management a lojei pe care le solicitați, inclusiv cotizații, prezență, transferuri și întâlniri online; (b) obligație legală — pentru a respecta legile aplicabile de contabilitate și audit; (c) interes legitim — pentru a proteja serviciul împotriva fraudei și a accesului neautorizat; (d) consimțământ — atunci când optați explicit pentru funcții precum notificările push sau transcrierea jurnalului vocal.",
          },
          {
            h: "4. Criptare & securitate",
            p: "Datele cu caracter personal (PII) — inclusiv numele, email-ul, telefonul și datele de contact — sunt criptate la nivel de câmp în baza de date folosind AES-256 cu o cheie per-tenant. Parolele sunt hash-uite cu bcrypt (factor cost 12). Autentificarea Multi-Factor (TOTP) este obligatorie pentru rolurile administrative. Tot traficul de rețea între dvs. și serverele noastre este criptat cu TLS 1.3 (HTTPS), iar domeniul nostru este înscris în lista de preîncărcare HSTS. Fiecare document generat de Serviciu (diplome, certificate, transferuri) este semnat cu un hash SHA-256 ce include IP-ul emitentului și marca temporală, creând un audit trail imuabil.",
          },
          {
            h: "5. Cine are acces",
            p: "Datele dvs. sunt vizibile doar pentru: (i) dvs.; (ii) ofițerii lojei căreia îi aparțineți (în roluri strict necesare, precum Secretarul sau Trezorierul); (iii) SuperAdministratorul Marii Loji din care face parte loja dvs., dacă este cazul; (iv) furnizorii noștri de infrastructură (MongoDB Atlas, providerul de hosting Emergent) acționând strict ca procesatori de date conform DPA-urilor semnate. Nu vindem și nu închiriem niciodată date personale, și nu le partajăm cu agenții de publicitate.",
          },
          {
            h: "6. Transferuri internaționale",
            p: "Datele sunt stocate în Spațiul Economic European. Dacă va trebui vreodată să transferăm date în afara SEE, ne vom baza pe deciziile de adecvare ale Comisiei Europene sau pe Clauzele Contractuale Standard și vă vom notifica în avans.",
          },
          {
            h: "7. Retenție",
            p: "Păstrăm datele dvs. cât timp contul lojei este activ. Când calitatea de membru se încheie sau solicitați ștergerea, ștergem sau anonimizăm complet datele dvs. în 30 de zile, cu excepția cazurilor în care păstrarea este cerută de legea contabilă sau de audit (de obicei 5–10 ani pentru înregistrările financiare), caz în care datele sunt mutate într-o arhivă restricționată accesibilă doar pentru conformitate legală.",
          },
          {
            h: "8. Drepturile dvs.",
            p: "Conform GDPR, aveți dreptul de: (a) a accesa datele personale; (b) a solicita rectificarea datelor inexacte; (c) a solicita ștergerea („dreptul de a fi uitat”); (d) a solicita restricționarea prelucrării; (e) portabilitatea datelor — a primi o copie a datelor în format machine-readable; (f) a obiecta la prelucrare; (g) a retrage consimțământul oricând; (h) a depune o plângere la o autoritate de supraveghere (în România: ANSPDCP). Pentru a exercita oricare dintre aceste drepturi, scrieți la contact@squareandcompass.store. Răspundem în termen de 30 de zile.",
          },
          {
            h: "9. Notificări push & analiză",
            p: "Notificările push sunt opționale și necesită consimțământul dvs. explicit la prima lansare. Puteți revoca acest consimțământ oricând din setările telefonului. Serviciul folosește analiză minimală first-party (număr de utilizări ale funcțiilor, jurnale de erori) — NU folosim Google Analytics, Facebook Pixel sau orice tracker terț de publicitate.",
          },
          {
            h: "10. Minori",
            p: "Serviciul nu este destinat utilizatorilor sub 18 ani. Lojile masonice sunt prin natura lor organizații pentru adulți. Nu colectăm cu bună știință date de la minori.",
          },
          {
            h: "11. Modificări ale Politicii",
            p: "Vă vom notifica modificările importante ale acestei Politici printr-o notificare în aplicație sau email cu cel puțin 14 zile înainte de intrarea lor în vigoare. Data ultimei revizuiri este afișată în partea de sus a acestei pagini.",
          },
          {
            h: "12. Contact",
            p: "Square & Compass Lodge Management · contact@squareandcompass.store · https://www.squareandcompass.store",
          },
        ],
      };

  return (
    <div className="auth-bg min-h-screen px-4 py-12">
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher />
      </div>

      <article
        className="mx-auto"
        style={{
          maxWidth: 880,
          background: "#fdf7e8",
          border: "1px solid #c9a86b",
          borderRadius: 8,
          padding: "3rem 3.5rem",
          color: "#0d1b2a",
          boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
        }}
        data-testid="privacy-policy-content"
      >
        <header className="mb-8">
          <p
            style={{
              color: "#9a7530",
              fontSize: "0.75rem",
              letterSpacing: "0.2em",
              fontFamily: "Cinzel, serif",
              textTransform: "uppercase",
              marginBottom: "0.5rem",
            }}
          >
            Square &amp; Compass — Lodge Management
          </p>
          <h1
            style={{
              fontFamily: "Cinzel, serif",
              fontSize: "2.5rem",
              fontWeight: 700,
              color: "#0d1b2a",
              marginBottom: "0.4rem",
              letterSpacing: "0.02em",
            }}
            data-testid="privacy-title"
          >
            {T.title}
          </h1>
          <p style={{ color: "#5a4d24", fontSize: "0.8rem" }}>
            {t("privacyPolicy.last_updated")}: {updatedDate}
          </p>
          <div
            style={{
              height: 1,
              width: 80,
              background: "#c9a86b",
              marginTop: "1.5rem",
            }}
          />
        </header>

        <p
          style={{
            fontSize: "0.95rem",
            lineHeight: 1.7,
            color: "#34322d",
            marginBottom: "2rem",
          }}
        >
          {T.intro}
        </p>

        {T.sections.map((sec, i) => (
          <section key={i} style={{ marginBottom: "1.8rem" }} data-testid={`privacy-section-${i}`}>
            <h2
              style={{
                fontFamily: "Cinzel, serif",
                fontSize: "1.1rem",
                color: "#0d1b2a",
                marginBottom: "0.6rem",
                letterSpacing: "0.04em",
              }}
            >
              {sec.h}
            </h2>
            <p style={{ fontSize: "0.92rem", lineHeight: 1.65, color: "#34322d" }}>
              {sec.p}
            </p>
          </section>
        ))}

        <footer
          style={{
            marginTop: "3rem",
            paddingTop: "1.5rem",
            borderTop: "1px solid #c9a86b",
            fontSize: "0.78rem",
            color: "#5a4d24",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>© 2026 Square &amp; Compass</span>
          <a
            href="/login"
            style={{ color: "#9a7530", textDecoration: "none" }}
            data-testid="back-to-login"
          >
            ← {t("privacyPolicy.back_to_login")}
          </a>
        </footer>
      </article>
    </div>
  );
}
